export type Attribution = {
  channel: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  gclid: string | null;
  fbclid: string | null;
  referrer: string | null;
  landing: string;
  ts: string;
};

function derive(): { obj: Attribution; signal: boolean } {
  const p = new URLSearchParams(window.location.search);
  const g = (k: string): string | null => {
    const v = p.get(k);
    return v && v.trim() ? v.trim() : null;
  };
  const utm_source = g('utm_source');
  const utm_medium = g('utm_medium');
  const utm_campaign = g('utm_campaign');
  const utm_term = g('utm_term');
  const utm_content = g('utm_content');
  const gclid = g('gclid');
  const fbclid = g('fbclid');

  let refHost = '';
  try {
    if (document.referrer) refHost = new URL(document.referrer).hostname.replace(/^www\./, '');
  } catch (e) {}
  const curHost = window.location.hostname.replace(/^www\./, '');
  const isInternal =
    !!refHost && (refHost === curHost || refHost === 'wonkybox.nz' || /\.wonkybox\.nz$/.test(refHost));
  const SEARCH = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'ecosia.', 'yandex.', 'baidu.'];
  const isSearch = !!refHost && SEARCH.some((s) => refHost.indexOf(s) !== -1);

  let channel: string;
  if (utm_source || utm_medium) channel = (utm_source || utm_medium)!.toLowerCase();
  else if (gclid) channel = 'google_ads';
  else if (fbclid) channel = 'facebook';
  else if (refHost && isSearch) channel = 'organic_search';
  else if (refHost && !isInternal) channel = 'referral';
  else channel = 'direct';

  return {
    obj: {
      channel,
      source: utm_source,
      medium: utm_medium,
      campaign: utm_campaign,
      term: utm_term,
      content: utm_content,
      gclid,
      fbclid,
      referrer: document.referrer || null,
      landing: window.location.pathname,
      ts: new Date().toISOString(),
    },
    signal: channel !== 'direct',
  };
}

function readCookie(name: string): Attribution | null {
  const m = document.cookie
    .split(';')
    .map((s) => s.trim())
    .find((r) => r.indexOf(name + '=') === 0);
  if (!m) return null;
  try {
    return JSON.parse(decodeURIComponent(m.split('=').slice(1).join('=')));
  } catch (e) {
    return null;
  }
}

function writeCookie(name: string, obj: Attribution, maxAge: number): void {
  document.cookie =
    name +
    '=' +
    encodeURIComponent(JSON.stringify(obj)) +
    '; domain=.wonkybox.nz; path=/; max-age=' +
    maxAge +
    '; SameSite=Lax' +
    (window.location.protocol === 'https:' ? '; Secure' : '');
}

// Reads first/last-touch attribution set by the Webflow snippet.
// Fallback: if no first-touch cookie exists (e.g. an ad linked straight to checkout,
// bypassing the marketing sites), derive from this page's own URL/referrer and persist.
export function getAttribution(): { first?: Attribution; last?: Attribution } {
  let first = readCookie('wb_ft');
  let last = readCookie('wb_lt');
  if (!first) {
    const d = derive();
    first = d.obj;
    writeCookie('wb_ft', d.obj, 63072000);
    if (d.signal) {
      writeCookie('wb_lt', d.obj, 7776000);
      if (!last) last = d.obj;
    }
  }
  return { first: first || undefined, last: last || undefined };
}
